import React, { Component } from 'react';
import { Row, Col, Modal, Form, FormControl } from 'react-bootstrap';
import { translate } from 'react-i18next';
import axios from 'axios';
import config from '../config';

/**
 * @name TemplateMediaPicker
 * @component Modal for chosing a template image
 */
export class TemplateMediaPicker extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedBackground: '',
            selectedLibraryUUID: '',
            mediaLibraries: '',
            media: [],
            subfolders: [],
            // Trail of folders navigated into: [{ uuid, name, organisationUUID }]
            // breadcrumb[0] is the top-level library chosen in the dropdown.
            breadcrumb: []
        };
    }

    componentDidMount() {
        this.loadLibrariesFromServer();
    }

    getMediaLibraryFromUUID(uuid) {
        const { mediaLibraries } = this.state;

        for (var i = 0; i < mediaLibraries.length; i++) {
            if (mediaLibraries[i].uuid === uuid) {
                return mediaLibraries[i];
            }
        }
        return null;
    }

    /**
     * @name loadLibrariesFromServer()
     * @desc Requests top-level template MediaLibraries for the dropdown, then opens the first one.
     */
    loadLibrariesFromServer = () => {
        var self = this;

        axios
            .get(
                `${config.baseURL}/templatelibraries`, {
                    params: {
                        page: 1,
                        limit: 1000
                    }
                }
            )
            .then(function (response) {
                self.setState({ mediaLibraries: response.data.mediaLibraries });
                if (response.data.mediaLibraries.length > 0) {
                    const first = response.data.mediaLibraries[0];
                    self.openTopLevelLibrary(first);
                }
            })
            .catch(function (error) {
                console.log(error);
            });
    };

    /**
     * @name loadFolderContents
     * @desc Fetches a folder's media and its direct sub-folders and puts them in state.
     * @param {Object} folder - { uuid, name, organisationUUID }
     */
    loadFolderContents = (folder) => {
        var self = this;
        axios
            .get(`${config.baseURL}/templatelibraries/${folder.uuid}/media`)
            .then(function (response) {
                self.setState({
                    media: response.data.media || [],
                    subfolders: response.data.subfolders || []
                });
            })
            .catch(function (error) {
                console.log(error);
            });
    };

    /**
     * @name openTopLevelLibrary
     * @desc Selecting a library from the dropdown: resets the breadcrumb to that library.
     */
    openTopLevelLibrary = (library) => {
        this.setState(
            {
                selectedLibraryUUID: library.uuid,
                breadcrumb: [
                    {
                        uuid: library.uuid,
                        name: library.name,
                        organisationUUID: library.organisationUUID
                    }
                ]
            },
            () => this.loadFolderContents(library)
        );
    };

    /**
     * @name handleSelectLibrary
     * @desc Dropdown change -> open the chosen top-level library.
     */
    handleSelectLibrary = e => {
        const library = this.getMediaLibraryFromUUID(e.target.value);
        if (library) {
            this.openTopLevelLibrary(library);
        }
    };

    /**
     * @name enterSubfolder
     * @desc Drill into a sub-folder: push it onto the breadcrumb and load its contents.
     */
    enterSubfolder = (subfolder) => {
        const folder = {
            uuid: subfolder.uuid,
            name: subfolder.name,
            organisationUUID: subfolder.organisationUUID
        };
        this.setState(
            { breadcrumb: [...this.state.breadcrumb, folder] },
            () => this.loadFolderContents(folder)
        );
    };

    /**
     * @name goToBreadcrumb
     * @desc Jump back up to an ancestor folder in the breadcrumb.
     */
    goToBreadcrumb = (index) => {
        const breadcrumb = this.state.breadcrumb.slice(0, index + 1);
        const folder = breadcrumb[breadcrumb.length - 1];
        this.setState(
            { breadcrumb },
            () => this.loadFolderContents(folder)
        );
    };

    render() {
        const {
            modalShow,
            handleModalClose,
            handleModalSave,
            handleSelectBackground,
            t
        } = this.props;
        const { mediaLibraries, selectedLibraryUUID, breadcrumb } = this.state;
        const libraryMap = Array.from(mediaLibraries);

        // The folder currently being viewed (top-level library or a sub-folder).
        const currentFolder = breadcrumb.length
            ? breadcrumb[breadcrumb.length - 1]
            : { uuid: null, organisationUUID: null };
        const organisationUUID = currentFolder.organisationUUID;
        const currentFolderUUID = currentFolder.uuid;

        const subfolders = this.state.subfolders || [];
        let thumbs = [];
        if (currentFolderUUID) {
            thumbs = this.state.media || [];
        }

        let self = this;

        return (
            <Modal
                show={modalShow}
                onHide={handleModalClose}
                dialogClassName="modal-80"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t('TemplateMediaPicker.modal_title')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row id="modal-form-wrapper">
                            <Col id="modal-form">
                                <p style={{ fontWeight: '700' }}>
                                    {t('TemplateMediaPicker.modal_form_library')}
                                </p>
                                <FormControl
                                    componentClass="select"
                                    value={selectedLibraryUUID}
                                    onChange={e => self.handleSelectLibrary(e)}
                                >
                                    {libraryMap.map(elem => (
                                        <option
                                            value={elem.uuid}
                                            key={elem.name}
                                            onClick={e =>
                                                self.handleSelectLibrary(e)
                                            }
                                        >
                                            {elem.name}
                                        </option>
                                    ))}
                                </FormControl>
                            </Col>
                        </Row>

                        {breadcrumb.length > 0 ? (
                            <Row>
                                <Col>
                                    <ol className="breadcrumb" style={{ marginTop: '10px', marginBottom: '10px' }}>
                                        {breadcrumb.map((crumb, index) => (
                                            index === breadcrumb.length - 1 ? (
                                                <li className="active" key={crumb.uuid}>
                                                    <i className="fas fa-folder-open" /> {crumb.name}
                                                </li>
                                            ) : (
                                                <li key={crumb.uuid}>
                                                    <a
                                                        href="#"
                                                        onClick={e => { e.preventDefault(); self.goToBreadcrumb(index); }}
                                                    >
                                                        <i className="fas fa-folder" /> {crumb.name}
                                                    </a>
                                                </li>
                                            )
                                        ))}
                                    </ol>
                                </Col>
                            </Row>
                        ) : null}

                        {currentFolderUUID ? (
                            <Row id="modal-bg-picker">
                                {subfolders.length > 0 ? (
                                    <div style={{ marginBottom: '15px' }}>
                                        <p style={{ fontWeight: '700' }}>
                                            {t('TemplateMediaPicker.modal_form_subfolders')}
                                        </p>
                                        <div className="modal-bg-thumbnail-group">
                                            {subfolders.map((subfolder) => (
                                                <div
                                                    key={subfolder.uuid}
                                                    className="modal-bg-thumbnail"
                                                    onClick={() => self.enterSubfolder(subfolder)}
                                                    style={{ cursor: 'pointer', textAlign: 'center', padding: '15px 5px' }}
                                                >
                                                    <i className="fas fa-folder" style={{ fontSize: '48px', color: '#f0ad4e' }} />
                                                    <br />
                                                    <small>{subfolder.name}</small>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : null}

                                <p style={{ fontWeight: '700' }}>
                                    {t('TemplateMediaPicker.modal_form_thumbs')}
                                </p>
                                <div className={`modal-bg-thumbnail-group`}>
                                    {thumbs.map((media) => (
                                        <div key={media.uuid} className={`modal-bg-thumbnail`} onClick={() => handleSelectBackground(media.uuid, `${`${organisationUUID}/${currentFolderUUID}/${media.uuid}.${media.extension}`}`)} style={{ position: 'relative' }}>
                                            <img src={`${config.playersBaseURL}/mediaserver/thumbnails/${organisationUUID}/${currentFolderUUID}/${media.uuid}.jpg`}>
                                            </img>
                                            {media.hasTextOverlay && (
                                                <span
                                                    className="label label-success"
                                                    style={{ position: 'absolute', top: '5px', right: '5px', fontSize: '10px' }}
                                                >
                                                    TEXT
                                                </span>
                                            )}
                                            <br/>
                                            <small>{`${media.name}`}</small>
                                        </div>
                                    ))}
                                </div>
                            </Row>
                        ) : null}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="btn btn-success"
                        onClick={handleModalSave}
                    >
                        <i className="fas fa-close fa-lg" />
                        <strong>
                            {' '}
                            {t('TemplateMediaPicker.modal_button_close')}
                        </strong>
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default translate('translations')(TemplateMediaPicker);
